// Based on Supabase auth.users table structure
export interface AuthUser {
  id: string; // UUID
  aud: string;
  role?: string;
  email?: string;
  email_confirmed_at?: string;
  phone?: string;
  confirmed_at?: string;
  last_sign_in_at?: string;
  app_metadata: Record<string, any>;
  user_metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
}

import { BaseEntity } from './base.entity';

// Extended user profile stored in public.users
export class UserProfile extends BaseEntity {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  current_project_id: string | null;
  language?: string;
  created_at: Date;
  updated_at: Date;

  constructor(data: {
    id: string;
    email: string;
    full_name: string | null;
    avatar_url: string | null;
    current_project_id: string | null;
    language?: string;
    created_at: Date;
    updated_at: Date;
  }) {
    super();
    this.id = data.id;
    this.email = data.email;
    this.full_name = data.full_name;
    this.avatar_url = data.avatar_url;
    this.current_project_id = data.current_project_id;
    this.language = data.language;
    this.created_at = data.created_at;
    this.updated_at = data.updated_at;
    this.setOriginalData(this.toData());
  }

  /**
   * Update user profile
   */
  updateProfile(fullName: string | null, avatarUrl: string | null): void {
    this.full_name = fullName;
    this.avatar_url = avatarUrl;
    this.updated_at = new Date();
    this.markAsDirty();
  }

  /**
   * Set current project
   */
  setCurrentProject(projectId: string | null): void {
    this.current_project_id = projectId;
    this.updated_at = new Date();
    this.markAsDirty();
  }

  /**
   * Set language preference
   */
  setLanguage(language: string): void {
    this.language = language;
    this.updated_at = new Date();
    this.markAsDirty();
  }

  /**
   * Convert to plain data object
   */
  toData(): any {
    return {
      id: this.id,
      email: this.email,
      full_name: this.full_name,
      avatar_url: this.avatar_url,
      current_project_id: this.current_project_id,
      language: this.language,
      created_at: this.created_at,
      updated_at: this.updated_at
    };
  }

  /**
   * Create UserProfile instance from database row
   */
  static fromData(data: any): UserProfile {
    return new UserProfile({
      id: data.id,
      email: data.email,
      full_name: data.full_name,
      avatar_url: data.avatar_url,
      current_project_id: data.current_project_id,
      language: data.language,
      created_at: new Date(data.created_at),
      updated_at: new Date(data.updated_at)
    });
  }
}

export interface UserProfileInsert {
  id: string; // UUID from auth.users
  email: string;
  full_name?: string | null;
  avatar_url?: string | null;
  current_project_id?: string | null;
  language?: string;
}

export interface UserProfileUpdate {
  full_name?: string | null;
  avatar_url?: string | null;
  current_project_id?: string | null;
  language?: string;
}